import { useState, useEffect, useCallback, useRef } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { customersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, PencilSimple, Trash, MagnifyingGlass, UploadSimple, UserCircle, Buildings,
} from '@phosphor-icons/react';
import CustomerDrillDownDialog from '@/components/CustomerDrillDownDialog';

const SALUTATIONS = ['Mr.', 'Ms.', 'Mrs.', 'Dr.'];
const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha',
  'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal', 'Chandigarh', 'Jammu and Kashmir', 'Ladakh',
  'Puducherry', 'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep',
];

const EMPTY_FORM = {
  customer_type: 'individual', salutation: 'Mr.', first_name: '', last_name: '',
  email: '', phone: '', gstin: '', place_of_supply: '', business_legal_name: '',
  business_trade_name: '', tax_preference: 'taxable',
};

const FormField = ({ label, children }) => (
  <div><label className="text-xs text-[#64748B] block mb-1">{label}</label>{children}</div>
);

export default function CustomersMasterPage() {
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editCust, setEditCust] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [drill, setDrill] = useState(null);
  const fileRef = useRef(null);
  const perPage = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (search) params.search = search;
      if (typeFilter) params.customer_type = typeFilter;
      const { data } = await customersAPI.list(params);
      setCustomers(data.customers || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  }, [page, search, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditCust(null); setForm({ ...EMPTY_FORM }); setShowForm(true); };
  const openEdit = (c) => {
    setEditCust(c);
    setForm({
      customer_type: c.customer_type || 'individual', salutation: c.salutation || '',
      first_name: c.first_name || '', last_name: c.last_name || '',
      email: c.email || '', phone: c.phone || '',
      gstin: c.gstin || '', place_of_supply: c.place_of_supply || '',
      business_legal_name: c.business_legal_name || '', business_trade_name: c.business_trade_name || '',
      tax_preference: c.tax_preference || 'taxable',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.first_name.trim()) { toast.error('First name is required'); return; }
    setSaving(true);
    try {
      if (editCust) {
        await customersAPI.update(editCust.id, form);
        toast.success('Customer updated');
      } else {
        await customersAPI.create(form);
        toast.success('Customer created');
      }
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await customersAPI.delete(id); toast.success('Customer deleted'); setDeleteConfirm(null); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data } = await customersAPI.bulkUpload(file);
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="p-6" data-testid="customers-master-page">
      <PageHeader title="Customers" subtitle="Manage individual and business customers" />

      <div className="mt-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search customers..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9" data-testid="customer-search" />
          </div>
          <Select value={typeFilter} onValueChange={v => { setTypeFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-[160px]" data-testid="customer-type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="individual">Individual</SelectItem>
              <SelectItem value="business">Business</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="add-customer-btn">
            <Plus size={16} className="mr-1.5" /> Add Customer
          </Button>
          <div className="relative">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" data-testid="customer-upload-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="customer-upload-btn">
              <UploadSimple size={16} className="mr-1.5" />{uploading ? 'Uploading...' : 'Bulk Upload'}
            </Button>
          </div>
        </div>

        <p className="text-sm text-[#64748B]">{total} customer{total !== 1 ? 's' : ''}</p>

        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#64748B] font-medium text-xs">
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Phone</th>
                <th className="text-left p-3">GSTIN</th>
                <th className="text-left p-3">Business Name</th>
                <th className="text-center p-3">Tax</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></td></tr>
              ) : customers.length > 0 ? customers.map(c => (
                <tr key={c.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors cursor-pointer" data-testid={`customer-row-${c.id}`} onClick={() => setDrill(c)}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {c.customer_type === 'business' ? <Buildings size={16} className="text-blue-600" /> : <UserCircle size={16} className="text-[#64748B]" />}
                      <span className="font-medium text-[#0F4C81] underline decoration-dotted underline-offset-2">{c.display_name}</span>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${c.customer_type === 'business' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                      {c.customer_type === 'business' ? 'Business' : 'Individual'}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-[#64748B]">{c.email || '-'}</td>
                  <td className="p-3 text-xs text-[#64748B]">{c.phone || '-'}</td>
                  <td className="p-3 text-xs text-[#64748B]">{c.gstin || '-'}</td>
                  <td className="p-3 text-xs text-[#64748B]">{c.business_legal_name || c.business_trade_name || '-'}</td>
                  <td className="p-3 text-center text-xs">{c.tax_preference === 'tax_exempt' ? 'Exempt' : 'Taxable'}</td>
                  <td className="p-3 text-center" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><PencilSimple size={16} /></Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteConfirm(c)} data-testid={`delete-customer-${c.id}`}><Trash size={16} /></Button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">No customers found. Click "Add Customer" to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-[#64748B]">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="customer-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">{editCust ? 'Edit Customer' : 'Add Customer'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Type */}
            <FormField label="Customer Type *">
              <div className="flex gap-4">
                {[['individual', 'Individual'], ['business', 'Business']].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="cust_type" checked={form.customer_type === v} onChange={() => setForm(f => ({ ...f, customer_type: v }))} className="accent-[#0F4C81]" data-testid={`ctype-${v}`} />
                    <span className="text-sm">{l}</span>
                  </label>
                ))}
              </div>
            </FormField>

            {/* Name */}
            <div className="grid grid-cols-4 gap-3">
              <FormField label="Salutation">
                <Select value={form.salutation} onValueChange={v => setForm(f => ({ ...f, salutation: v }))}>
                  <SelectTrigger data-testid="customer-salutation"><SelectValue placeholder="--" /></SelectTrigger>
                  <SelectContent>{SALUTATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
              <div className="col-span-2">
                <FormField label="First Name *"><Input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} placeholder="First name" data-testid="customer-first-name" /></FormField>
              </div>
              <FormField label="Last Name"><Input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} placeholder="Last name" data-testid="customer-last-name" /></FormField>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Email"><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" data-testid="customer-email" /></FormField>
              <FormField label="Phone"><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91 XXXXXXXXXX" data-testid="customer-phone" /></FormField>
            </div>

            {/* Business fields */}
            {form.customer_type === 'business' && (
              <div className="bg-[#F8FAFC] rounded-lg p-4 space-y-3">
                <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Business Details</p>
                <FormField label="GSTIN"><Input value={form.gstin} onChange={e => setForm(f => ({ ...f, gstin: e.target.value }))} placeholder="22AAAAA0000A1Z5" data-testid="customer-gstin" /></FormField>
                <FormField label="Place of Supply">
                  <Select value={form.place_of_supply} onValueChange={v => setForm(f => ({ ...f, place_of_supply: v }))}>
                    <SelectTrigger data-testid="customer-supply"><SelectValue placeholder="Select state..." /></SelectTrigger>
                    <SelectContent>{STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Business Legal Name"><Input value={form.business_legal_name} onChange={e => setForm(f => ({ ...f, business_legal_name: e.target.value }))} data-testid="customer-legal-name" /></FormField>
                  <FormField label="Business Trade Name"><Input value={form.business_trade_name} onChange={e => setForm(f => ({ ...f, business_trade_name: e.target.value }))} data-testid="customer-trade-name" /></FormField>
                </div>
                <FormField label="Tax Preference">
                  <div className="flex gap-4">
                    {[['taxable', 'Taxable'], ['tax_exempt', 'Tax Exempt']].map(([v, l]) => (
                      <label key={v} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="tax_pref" checked={form.tax_preference === v} onChange={() => setForm(f => ({ ...f, tax_preference: v }))} className="accent-[#0F4C81]" data-testid={`tax-pref-${v}`} />
                        <span className="text-sm">{l}</span>
                      </label>
                    ))}
                  </div>
                </FormField>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="customer-save-btn">
              {saving ? 'Saving...' : editCust ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={o => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-customer-dialog">
          <DialogHeader><DialogTitle className="font-['Plus_Jakarta_Sans']">Delete Customer</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B] py-2">Delete <strong>{deleteConfirm?.display_name}</strong>? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-customer">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Transaction Detail */}
      <CustomerDrillDownDialog
        open={!!drill}
        onOpenChange={o => { if (!o) setDrill(null); }}
        customerId={drill?.id || ''}
        customerName={drill?.display_name || ''}
        customerPhone={drill?.phone || ''}
      />
    </div>
  );
}
